import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import OnboardingField from '../../components/onboarding/OnboardingField'
import BioCalibrationOnboarding, { type BioCalibrationPayload } from '../../components/onboarding/BioCalibrationOnboarding'
import { buildBioPayloadFromInitialData, parseInitialInventory } from '../../lib/calibration/onboardingConstants'
import { scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingChipClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import { formStateToMedications, medicationsToFormState } from '../../lib/profile'

import type { OnboardingStepProps } from './stepProps'

const EQUIPMENT_OPTIONS = [
  { value: 'Gym', key: 'gym' },
  { value: 'Dumbbells', key: 'dumbbells' },
  { value: 'Bands', key: 'bands' },
  { value: 'Barbell', key: 'barbell' },
  { value: 'Cardio', key: 'cardio' },
  { value: 'Bodyweight', key: 'bodyweight' },
] as const

const Step3 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const medicationForm = medicationsToFormState(initialData?.medications)
  const [bioComplete, setBioComplete] = useState(Boolean(initialData?.orthopedic_injury_inventory))
  const [bioPayload, setBioPayload] = useState<BioCalibrationPayload | null>(() =>
    buildBioPayloadFromInitialData(
      initialData ?? {},
      parseInitialInventory(initialData?.orthopedic_injury_inventory),
    ),
  )
  const [formData, setFormData] = useState({
    past_injuries: (initialData?.past_injuries as string) || '',
    equipment_available: (initialData?.equipment_available as string[]) || [],
    medications: medicationForm.flag,
    medication_details: medicationForm.details,
  })
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set())

  const handleSubmit = () => {
    setError('')

    const calibration = bioPayload ?? buildBioPayloadFromInitialData(initialData ?? {}, {})
    if (!calibration) {
      setInvalidFields(new Set(['bio_calibration']))
      setError(t('onboarding.biomechanics.completeCalibrationFirst'))
      scrollToFirstFieldError(['bio_calibration'])
      return
    }

    if (formData.equipment_available.length === 0) {
      setInvalidFields(new Set(['equipment_available']))
      setError(t('onboarding.biomechanics.selectEquipment'))
      scrollToFirstFieldError(['equipment_available'])
      return
    }

    setInvalidFields(new Set())
    onNext({
      dob: calibration.dob,
      training_goal: calibration.training_goal,
      training_age: calibration.training_age,
      orthopedic_injury_inventory: calibration.orthopedic_injury_inventory,
      calibration_status: calibration.calibration_status,
      medical_niggles: null,
      past_injuries: formData.past_injuries,
      equipment_available: formData.equipment_available,
      medications: formStateToMedications(formData.medications, formData.medication_details),
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const toggleEquipment = (item: string) => {
    setFormData((prev) => ({
      ...prev,
      equipment_available: prev.equipment_available.includes(item)
        ? prev.equipment_available.filter((e) => e !== item)
        : [...prev.equipment_available, item],
    }))
    setInvalidFields((prev) => {
      if (!prev.has('equipment_available')) return prev
      const next = new Set(prev)
      next.delete('equipment_available')
      return next
    })
  }

  const flaggedCount = Object.keys(bioPayload?.orthopedic_injury_inventory ?? {}).length

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleSubmit} onPrev={onPrevious}>
      <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.biomechanics.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.biomechanics.subtitle')}</p>
        </div>

        {!bioComplete ? (
          <div
            id="field-bio_calibration"
            className={`scroll-mt-32 rounded-2xl transition-shadow ${
              invalidFields.has('bio_calibration') ? 'ring-2 ring-kilo-danger/60 ring-offset-2 ring-offset-kilo-card' : ''
            }`}
          >
            <BioCalibrationOnboarding
              initialData={initialData}
              onComplete={(payload) => {
                setBioPayload(payload)
                setBioComplete(true)
                setInvalidFields((prev) => {
                  if (!prev.has('bio_calibration')) return prev
                  const next = new Set(prev)
                  next.delete('bio_calibration')
                  return next
                })
              }}
            />
          </div>
        ) : (
          <div className="rounded-2xl border border-kilo-accent/30 bg-kilo-accent/5 px-4 py-3 text-sm text-kilo-accent">
            {t('onboarding.biomechanics.mapSaved')} · {t('onboarding.biomechanics.nodesFlagged', { count: flaggedCount })}
            <button
              type="button"
              onClick={() => setBioComplete(false)}
              className="ml-2 text-xs underline opacity-80 hover:opacity-100"
            >
              {t('onboarding.biomechanics.edit')}
            </button>
          </div>
        )}

        {bioComplete ? (
          <>
            <label className="space-y-2">
              <span className={ONBOARDING.label}>{t('onboarding.biomechanics.pastInjuries')}</span>
              <textarea
                name="past_injuries"
                value={formData.past_injuries}
                onChange={handleChange}
                rows={3}
                className={ONBOARDING.textarea}
                placeholder={t('onboarding.biomechanics.pastInjuriesPlaceholder')}
              />
            </label>

            <OnboardingField id="equipment_available" label={t('onboarding.biomechanics.equipment')} required invalid={invalidFields.has('equipment_available')}>
              <div className="grid gap-2 sm:grid-cols-2">
                {EQUIPMENT_OPTIONS.map(({ value, key }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleEquipment(value)}
                    className={onboardingChipClass(formData.equipment_available.includes(value))}
                  >
                    {t(`onboarding.biomechanics.equipmentOptions.${key}`)}
                  </button>
                ))}
              </div>
            </OnboardingField>

            <label className="space-y-2">
              <span className={ONBOARDING.label}>{t('onboarding.biomechanics.medications')}</span>
              <select name="medications" value={formData.medications} onChange={handleChange} className={ONBOARDING.select}>
                <option value="false">{t('onboarding.form.no')}</option>
                <option value="true">{t('onboarding.form.yes')}</option>
              </select>
            </label>

            {formData.medications === 'true' ? (
              <label className="space-y-2">
                <span className={ONBOARDING.label}>{t('onboarding.biomechanics.medicationDetails')}</span>
                <textarea
                  name="medication_details"
                  value={formData.medication_details}
                  onChange={handleChange}
                  rows={3}
                  className={ONBOARDING.textarea}
                  placeholder={t('onboarding.biomechanics.medicationPlaceholder')}
                />
              </label>
            ) : null}
          </>
        ) : null}

        {error ? <div className={ONBOARDING.error}>{error}</div> : null}
      </form>
    </OnboardingLayout>
  )
}

export default Step3
